// ==================== SERVICIO DE SUBCONTRATOS ====================
// Maneja todas las operaciones CRUD de subcontratos

import api, { ejecutarConReintentos } from './api';
import authService from './authService';

const subcontratosService = {
  // ==================== OBTENER TODOS LOS SUBCONTRATOS ====================
  async obtenerSubcontratos(completada = false) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.get('/subcontratos', {
          params: { completada },
        });
      });

      return response.data.subcontratos || [];
    } catch (error) {
      console.error('❌ Error al obtener subcontratos:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al obtener subcontratos' };
    }
  },

  // ==================== OBTENER UN SUBCONTRATO ====================
  async obtenerSubcontrato(idSubcontrato) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.get(`/subcontratos/${idSubcontrato}`);
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al obtener subcontrato:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al obtener subcontrato' };
    }
  },

  // ==================== CREAR SUBCONTRATO ====================
  async crearSubcontrato(datosSubcontrato) {
    try {
      // Validación local antes de enviar
      if (!datosSubcontrato.text || !datosSubcontrato.madeBy || !datosSubcontrato.specialty || !datosSubcontrato.dueDate) {
        throw new Error('Faltan campos requeridos: text, madeBy, specialty, dueDate');
      }

      const response = await ejecutarConReintentos(async () => {
        return await api.post('/subcontratos', datosSubcontrato);
      }, 3); // 3 reintentos para crear subcontrato

      return response.data.subcontrato;
    } catch (error) {
      console.error('❌ Error al crear subcontrato:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al crear subcontrato' };
    }
  },

  // ==================== ACTUALIZAR SUBCONTRATO ====================
  async actualizarSubcontrato(idSubcontrato, datosActualizados) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.put(`/subcontratos/${idSubcontrato}`, datosActualizados);
      });
      return response.data.subcontrato;
    } catch (error) {
      console.error('❌ Error al actualizar subcontrato:', error);
      throw error;
    }
  },

  // ==================== COMPLETAR SUBCONTRATO ====================
  async completarSubcontrato(idSubcontrato) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.post(`/subcontratos-completados/completar/${idSubcontrato}`);
      });
      return response.data.subcontratoCompletado;
    } catch (error) {
      console.error('❌ Error al completar subcontrato:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al completar subcontrato' };
    }
  },

  // ==================== ELIMINAR SUBCONTRATO ====================
  async eliminarSubcontrato(idSubcontrato) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.delete(`/subcontratos/${idSubcontrato}`);
      });
      return response.data;
    } catch (error) {
      // No loguear errores 404, son esperados cuando el subcontrato ya fue eliminado
      if (error.response?.status !== 404) {
        console.error('❌ Error al eliminar subcontrato:', error.response?.data || error.message);
      }
      throw error;
    }
  },

  // ==================== MARCAR PROCESO COMPLETADO ====================
  async marcarProcesoCompletado(idSubcontrato, nombreEtapa, datos) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.put(`/subcontratos/${idSubcontrato}/procesos/${nombreEtapa}`, datos);
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al marcar proceso:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al marcar proceso' };
    }
  },

  // ==================== DESCOMPLETAR PROCESO ====================
  async descompletarProceso(idSubcontrato, nombreEtapa) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.put(`/subcontratos/${idSubcontrato}/descompletar-proceso/${nombreEtapa}`);
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al descompletar proceso:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al descompletar proceso' };
    }
  },

  // ==================== OBTENER SUBCONTRATOS CRÍTICOS ====================
  async obtenerSubcontratosCriticos(limite = 15) {
    try {
      const proyectoId = authService.obtenerProyectoId();

      if (!proyectoId) {
        throw new Error('No hay proyecto seleccionado');
      }

      const response = await api.get(`/subcontratos/criticas/${proyectoId}`, {
        params: { limite },
      });

      return response.data.subcontratos || [];
    } catch (error) {
      console.error('Error al obtener subcontratos críticos:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al obtener subcontratos críticos' };
    }
  },

  // ==================== OBTENER SUBCONTRATOS COMPLETADOS ====================
  async obtenerSubcontratosCompletados() {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.get('/subcontratos-completados');
      });
      return response.data.subcontratos || [];
    } catch (error) {
      console.error('❌ Error al obtener subcontratos completados:', error.response?.data || error.message);
      throw error.response?.data || { error: 'Error al obtener subcontratos completados' };
    }
  },

  // ==================== ELIMINAR SUBCONTRATO COMPLETADO ====================
  async eliminarSubcontratoCompletado(idSubcontrato) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.delete(`/subcontratos-completados/${idSubcontrato}`);
      });
      return response.data;
    } catch (error) {
      // No loguear errores 404, son esperados cuando el subcontrato ya fue eliminado
      if (error.response?.status !== 404) {
        console.error('❌ Error al eliminar subcontrato completado:', error.response?.data || error.message);
      }
      throw error;
    }
  },

  // ==================== RESTAURAR SUBCONTRATO COMPLETADO A ACTIVOS ====================
  async restaurarSubcontrato(idSubcontrato) {
    try {
      const response = await ejecutarConReintentos(async () => {
        return await api.post(`/subcontratos-completados/restaurar/${idSubcontrato}`);
      });
      return response.data;
    } catch (error) {
      console.error('❌ Error al restaurar subcontrato:', error.response?.data || error.message);
      throw error;
    }
  },
};

export default subcontratosService;
